import { useEffect, useState } from "react";
import { toast } from "sonner";
import { cCourseRoster, cSetGrade, dueLabel } from "@/lib/api";

// COURSE ROSTER — every student's name and email; for each assignment a grade the teacher types, and the final Composition (0–4) and
// Thinking Structure (1–7) levels filled in automatically from TWS's analysis of the student's latest version. Levels are never editable.
const STATUS = { not_started: "Not started", in_progress: "In progress", complete: "Completed" };

function GradeCell({ courseId, cell, student, onSaved }) {
  const [v, setV] = useState(cell.grade || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(cell.grade || ""), [cell.grade]);
  const save = async () => {
    if (v.trim() === (cell.grade || "")) return;
    setSaving(true);
    try { const r = await cSetGrade(courseId, { assignment_id: cell.assignment_id, student_id: student.id, grade: v.trim() }); onSaved(r); toast.success(r.grade ? `Grade saved for ${student.name}` : "Grade cleared"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save the grade"); setV(cell.grade || ""); }
    finally { setSaving(false); }
  };
  return (
    <input data-testid={`grade-${student.id}-${cell.assignment_id}`} value={v} onChange={(e) => setV(e.target.value)} onBlur={save} disabled={saving}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setV(cell.grade || ""); }}
      placeholder="—" aria-label={`Grade for ${student.name}`}
      className="w-16 border border-stone-200 rounded-sm px-2 py-1 text-[13px] text-center bg-white focus:outline-none focus:border-[#8C3A2A] placeholder:text-stone-300" />
  );
}

const Level = ({ value, max, title, testid }) => value === null || value === undefined
  ? <span data-testid={testid} className="text-stone-300">—</span>
  : <span data-testid={testid} title={title} className="font-mono text-[12.5px] text-stone-800">{Number(value).toFixed(value % 1 ? 1 : 0)}<span className="text-stone-400 text-[10px]">/{max}</span></span>;

export default function CourseRoster({ courseId, onOpenSubmission }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const load = () => cCourseRoster(courseId).then(setR).catch((e) => setErr(e?.response?.data?.detail || "Could not load the roster"));
  useEffect(() => { load(); }, [courseId]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <p data-testid="roster-error" className="text-[13px] text-[#8C3A2A]">{err}</p>;
  if (!r) return <p className="text-[13px] text-stone-400">Loading roster…</p>;
  const onSaved = (g) => setR((prev) => ({ ...prev, rows: prev.rows.map((row) => row.student.id !== g.student_id ? row : { ...row, cells: row.cells.map((c) => c.assignment_id === g.assignment_id ? { ...c, grade: g.grade } : c) }) }));
  const csv = () => {
    const head = ["Student", "Email", ...r.assignments.flatMap((a) => [`${a.title} — Grade`, `${a.title} — Composition (0–4)`, `${a.title} — Thinking Structure (1–7)`, `${a.title} — Status`])];
    const lines = r.rows.map((row) => [row.student.name, row.student.email, ...row.cells.flatMap((c) => [c.grade, c.composition ?? "", c.thinking ?? "", STATUS[c.status] || c.status])]);
    const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const blob = new Blob([[head, ...lines].map((l) => l.map(esc).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${r.course.title.replace(/[^\w]+/g, "_")}_roster.csv`; a.click();
  };
  return (
    <div data-testid="course-roster">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl text-stone-900">Roster</h1>
          <p className="mt-1 text-[13px] text-stone-600 max-w-2xl">Names and emails of everyone in {r.course.title}. Type a grade in any cell (a number, a letter, a word — press Enter or click away to save).
            <span className="text-stone-500"> Composition</span> (0–4) and <span className="text-stone-500">Thinking Structure</span> (1–7) are filled in automatically from the student's latest version and cannot be edited.</p>
        </div>
        <button data-testid="roster-csv" onClick={csv} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2">Download CSV</button>
      </div>
      <div className="mt-4 overflow-x-auto bg-white border border-stone-200 rounded-md">
        <table className="min-w-full border-collapse text-left text-[13px]">
          <thead>
            <tr className="border-b border-stone-200 text-[10.5px] uppercase tracking-wider text-stone-500">
              <th rowSpan={2} className="px-4 py-2 align-bottom sticky left-0 bg-white">Student</th>
              {r.assignments.map((a) => (
                <th key={a.id} colSpan={3} data-testid={`roster-col-${a.id}`} className="px-3 py-2 text-center border-l border-stone-100">
                  <span className="normal-case tracking-normal text-[12px] text-stone-800 font-medium">{a.title}</span>
                  {a.due_date ? <span className="block normal-case tracking-normal text-[10.5px] text-stone-400">{dueLabel(a.due_date)}</span> : null}
                </th>
              ))}
              {!r.assignments.length && <th className="px-4 py-2 font-normal normal-case tracking-normal text-stone-400">No assignments yet</th>}
            </tr>
            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-400">
              {r.assignments.map((a) => (
                <FragmentHead key={a.id} />
              ))}
            </tr>
          </thead>
          <tbody>
            {r.rows.map((row) => (
              <tr key={row.student.id} data-testid={`roster-row-${row.student.id}`} className="border-b border-stone-100 align-top">
                <th scope="row" className="px-4 py-2.5 font-normal sticky left-0 bg-white">
                  <div className="text-stone-900">{row.student.name}</div>
                  <div data-testid={`roster-email-${row.student.id}`} className="text-[12px] text-stone-500 break-all">{row.student.email}</div>
                </th>
                {row.cells.map((c) => (
                  <RosterCells key={c.assignment_id} courseId={courseId} cell={c} student={row.student} onSaved={onSaved} onOpen={onOpenSubmission} />
                ))}
              </tr>
            ))}
            {!r.rows.length && <tr><td colSpan={1 + r.assignments.length * 3} className="px-4 py-6 text-center text-[13px] text-stone-400">No students yet — share the join code.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FragmentHead = () => (<>
  <th className="px-3 py-1.5 text-center border-l border-stone-100">Grade</th>
  <th className="px-3 py-1.5 text-center">Composition</th>
  <th className="px-3 py-1.5 text-center">Thinking</th>
</>);

function RosterCells({ courseId, cell, student, onSaved, onOpen }) {
  const open = cell.session_id && onOpen ? () => onOpen(cell.session_id) : null;
  return (<>
    <td className="px-3 py-2 text-center border-l border-stone-100">
      <GradeCell courseId={courseId} cell={cell} student={student} onSaved={onSaved} />
      <div className="mt-1 text-[10px] text-stone-400">
        {open ? <button data-testid={`roster-open-${student.id}-${cell.assignment_id}`} onClick={open} className="hover:text-[#8C3A2A] underline underline-offset-2">{STATUS[cell.status] || cell.status}</button> : (STATUS[cell.status] || cell.status)}
      </div>
    </td>
    <td className="px-3 py-2 text-center"><Level value={cell.composition} max={4} title="Final Composition level (WSR, 0–4)" testid={`roster-comp-${student.id}-${cell.assignment_id}`} /></td>
    <td className="px-3 py-2 text-center"><Level value={cell.thinking} max={7} title={cell.thinking_form || "Final Thinking Structure coordinate (1–7)"} testid={`roster-think-${student.id}-${cell.assignment_id}`} />
      {cell.thinking_form ? <div className="text-[10px] text-stone-400 max-w-[120px] mx-auto leading-tight">{cell.thinking_form.split(" · ")[0]}</div> : null}</td>
  </>);
}
